import React, { useState, useEffect, useCallback } from 'react';
import { RotateCcw, Lock } from 'lucide-react';
import { supabase } from './supabaseClient';
import './FloorPlan.css';

// Floor plan for events that sell numbered tables. The room itself lives in
// event_tables (zone + sort); who holds what comes from the event_table_map RPC.
// Claiming is done by a database trigger when the card is saved, never from here.

// Rows that stand back to back in the room; walkways run between the groups.
const ISLANDS = [['top'], ['r60', 'r54'], ['r48', 'r42'], ['r36', 'r30']];

// Which of these events have a floor plan. Returns a Set of event ids.
export function useFloorPlanEventIds(eventIds) {
  const [ids, setIds] = useState(() => new Set());
  const key = (eventIds || []).filter(Boolean).join(',');
  useEffect(() => {
    if (!key) { setIds(new Set()); return; }
    let cancelled = false;
    supabase
      .from('event_tables')
      .select('event_id')
      .in('event_id', key.split(','))
      .limit(1000)
      .then(({ data }) => {
        if (cancelled) return;
        setIds(new Set((data || []).map(r => r.event_id)));
      });
    return () => { cancelled = true; };
  }, [key]);
  return ids;
}

// null while checking, then true / false.
export function useHasFloorPlan(eventId) {
  const [has, setHas] = useState(null);
  useEffect(() => {
    if (!eventId) { setHas(false); return; }
    let cancelled = false;
    supabase
      .from('event_tables')
      .select('id', { count: 'exact', head: true })
      .eq('event_id', eventId)
      .then(({ count, error }) => { if (!cancelled) setHas(error ? false : (count || 0) > 0); });
    return () => { cancelled = true; };
  }, [eventId]);
  return has;
}

export function useEventTableMap(eventId) {
  const [tables, setTables] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const reload = useCallback(async () => {
    if (!eventId) return [];
    setLoading(true);
    setError('');
    const { data, error: err } = await supabase.rpc('event_table_map', { p_event: eventId });
    if (err) { setError('Could not load the floor plan.'); setLoading(false); return []; }
    setTables(data || []);
    setLoading(false);
    return data || [];
  }, [eventId]);
  useEffect(() => { reload(); }, [reload]);
  return { tables, loading, error, reload };
}

const bySort = (a, b) => (a.sort - b.sort) || (a.number - b.number);

// The room. getCell(table) -> { cls, disabled, label (aria), sub (small text) }.
export function FloorPlanRoom({ tables, getCell, onTap, staff = false }) {
  const zone = (z) => tables.filter(t => t.zone === z).sort(bySort);

  const cell = (t) => {
    const c = getCell(t);
    return (
      <button
        key={t.table_id}
        type="button"
        className={`fp-table ${c.cls}`}
        disabled={c.disabled}
        aria-label={c.label}
        aria-pressed={c.pressed}
        onClick={() => onTap && onTap(t)}
      >
        <span>{t.number}</span>
        {c.locked ? <Lock size={11} aria-hidden="true" style={{ marginTop: '2px' }} /> : null}
        {c.sub ? <span className="fp-name">{c.sub}</span> : null}
      </button>
    );
  };

  const row = (z, extra) => {
    const list = zone(z);
    if (!list.length) return null;
    return (
      <div key={z} className={`fp-row ${extra || ''}`} style={{ gridTemplateColumns: `repeat(${list.length}, minmax(0, 1fr))` }}>
        {list.map(cell)}
      </div>
    );
  };

  return (
    <div className={`fp-room ${staff ? 'fp-staff' : ''}`}>
      <div className="fp-wall">
        <div className="fp-wall-upper-left fp-stack">{zone('leftUpper').map(cell)}</div>
        <div className="fp-opening" aria-hidden="true">ENTRANCE<br />EXIT</div>
        <div className="fp-stack">{zone('leftLower').map(cell)}</div>
      </div>
      <div className="fp-center">
        {/* Like the real room: the first row sits right against the stage, then
            back-to-back pairs of rows as islands with walkways between them,
            then the back wall row tucked between the corner tables. */}
        <div className="fp-front">
          <div className="fp-stage">STAGE</div>
          {row('top', 'fp-island-row fp-island fp-under-stage')}
        </div>
        {ISLANDS.filter((g) => g[0] !== 'top').map((group) => {
          const rows = group.map((z) => row(z, 'fp-island-row')).filter(Boolean);
          return rows.length ? <div key={group.join('-')} className="fp-island">{rows}</div> : null;
        })}
        {row('bottom', 'fp-bottom')}
      </div>
      <div className="fp-wall">
        <div className="fp-opening fp-fixed" aria-hidden="true">REST<br />ROOM</div>
        <div className="fp-stack">{zone('rightUpper').map(cell)}</div>
        <div className="fp-opening fp-plain" aria-hidden="true" />
        <div className="fp-stack">{zone('rightLower').map(cell)}</div>
      </div>
    </div>
  );
}

export function FloorPlanLegend({ staff = false, showSelected = false, showHeld = true }) {
  return (
    <div className={`fp-legend ${staff ? 'fp-staff-legend' : ''}`}>
      <div><span className="fp-chip open" />Open</div>
      {showSelected && <div><span className="fp-chip sel" />Yours</div>}
      {showHeld && <div><span className="fp-chip held" />Held</div>}
      <div><span className="fp-chip taken" />Taken</div>
      {staff && <div><span className="fp-chip blocked" />Reserved</div>}
    </div>
  );
}

// Vendor-facing picker. selected is an array of table ids, onChange gets the new array.
export function FloorPlanPicker({ eventId, selected, onChange, tableFeeCents = 0, label, maxSelect }) {
  const { tables, loading, error, reload } = useEventTableMap(eventId);
  const [notice, setNotice] = useState('');
  const fee = tableFeeCents || 0;

  const toggle = (t) => {
    if (t.state !== 'open') return;
    setNotice('');
    if (maxSelect && !selected.includes(t.table_id) && selected.length >= maxSelect) {
      setNotice(`Your card covers ${maxSelect} ${maxSelect === 1 ? 'table' : 'tables'}. Tap one of yours to swap it.`);
      return;
    }
    onChange(selected.includes(t.table_id) ? selected.filter(x => x !== t.table_id) : [...selected, t.table_id]);
  };

  const refresh = async () => {
    const fresh = await reload();
    const open = new Set((fresh || []).filter(t => t.state === 'open').map(t => t.table_id));
    const kept = selected.filter(id => open.has(id));
    if (kept.length !== selected.length) {
      onChange(kept);
      setNotice('Someone just took one of your tables. It was taken off your list.');
    }
  };

  const numbers = tables
    .filter(t => selected.includes(t.table_id))
    .map(t => t.number)
    .sort((a, b) => a - b);
  const money = (c) => `$${(c / 100).toFixed(c % 100 === 0 ? 0 : 2)}`;

  return (
    <div className="fp" style={{ maxWidth: '560px', margin: '0 auto' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: '10px', marginBottom: '10px', flexWrap: 'wrap' }}>
        <FloorPlanLegend showSelected />
        <button
          type="button"
          onClick={refresh}
          disabled={loading}
          style={{
            display: 'inline-flex', alignItems: 'center', gap: '6px', background: '#fff', color: '#374151',
            border: '1px solid #d1d5db', borderRadius: '8px', padding: '6px 10px', fontSize: '12px',
            fontWeight: 700, fontFamily: 'inherit', cursor: loading ? 'wait' : 'pointer', touchAction: 'manipulation',
          }}
        >
          <RotateCcw size={13} /> Refresh
        </button>
      </div>
      {error && <p style={{ color: '#dc2626', fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>{error}</p>}
      {notice && <p style={{ color: '#92400e', fontSize: '0.85rem', fontWeight: 700, margin: '0 0 8px' }}>{notice}</p>}
      <FloorPlanRoom
        tables={tables}
        onTap={toggle}
        getCell={(t) => {
          const on = selected.includes(t.table_id);
          if (on) return { cls: 'fp-selected', label: `Table ${t.number}, selected`, pressed: true };
          if (t.state === 'open') return { cls: 'fp-open', label: `Table ${t.number}, open`, pressed: false };
          return {
            cls: t.state === 'held' ? 'fp-held' : 'fp-taken',
            disabled: true,
            label: `Table ${t.number}, ${t.state}`,
          };
        }}
      />
      <div style={{
        marginTop: '12px', padding: '12px 14px', borderRadius: '10px',
        backgroundColor: selected.length ? '#fef2f2' : '#f9fafb',
        border: selected.length ? '1px solid #fecaca' : '1px solid #e5e7eb',
      }}>
        <div style={{ fontSize: '0.95rem', fontWeight: 800, color: '#1a1a1a' }}>
          {selected.length === 0
            ? (label || 'Tap the tables you want.')
            : fee > 0
              ? `${selected.length} ${selected.length === 1 ? 'table' : 'tables'} x ${money(fee)} = ${money(selected.length * fee)}`
              : `${selected.length} ${selected.length === 1 ? 'table' : 'tables'}`}
        </div>
        {selected.length > 0 && (
          <div style={{ fontSize: '0.82rem', color: '#6b7280', marginTop: '3px' }}>
            Table{numbers.length === 1 ? '' : 's'} {numbers.join(', ')}.{' '}
            <button
              type="button"
              onClick={() => onChange([])}
              style={{ background: 'none', border: 'none', padding: 0, color: '#C8102E', fontWeight: 700, fontSize: '0.82rem', cursor: 'pointer', fontFamily: 'inherit' }}
            >
              Clear
            </button>
          </div>
        )}
        <div style={{ fontSize: '0.78rem', color: '#6b7280', marginTop: '6px', lineHeight: 1.45 }}>
          Your tables are held for you as soon as your card is saved. Nothing is charged until you are approved.
        </div>
      </div>
    </div>
  );
}
